import React from "react";
import { CheckOutlined, CloseOutlined, ExportOutlined, MessageOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Title } from "@tremor/react";
import { Badge, Button, message, Segmented, Space, Table, Tag, Tooltip, Typography, type TableProps } from "antd";
import { type FilterValue } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { chatActions } from "@fitness-app/app-store";
import { getTrainersTeam } from "@fitness-app/app-store/src/store/reducers/team/selectors";
import {
  type ChatMessageMetadata,
  type ChatMessageMetadataWithTrainee,
  type MessageSentiment,
} from "@fitness-app/data-models/entities/Chat";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePermissionRequirement } from "~/hooks/usePermission";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { useTeamMembers } from "~/modules/Team/hooks/useTeamMembers";
import { createTrainerOptions } from "~/modules/Trainee/helpers/createTrainerOptions";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";

/** Wejście na dashboard ma wyglądać jak przed TRE-1936 — filtr tylko rozszerza widok. */
export const DEFAULT_ALERTS_SENTIMENT: MessageSentiment[] = ["highly_negative"];

/** „Mój zespół" (swoje klientki i klientki swoich trenerów) albo całe konto. */
export type ChatAlertsTeamScope = "team" | "account";

export const useChatAlerts = (enabled = true) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const assignedTrainers = useAppSelector((store) => store.team.assignedTrainers);
  const [sentiment, setSentiment] = React.useState<MessageSentiment[]>(DEFAULT_ALERTS_SENTIMENT);
  const [teamScope, setTeamScope] = React.useState<ChatAlertsTeamScope>("team");

  const coachedTrainerIds = React.useMemo(
    () => (memberId ? [memberId, ...assignedTrainers.map((assigned) => assigned.assignedTrainerId)] : []),
    [memberId, assignedTrainers],
  );
  // Przełącznik ma sens tylko dla kogoś, kto prowadzi trenerów: dla zwykłej
  // trenerki „mój zespół" i tak jest wszystkim, co widzi, a właściciel i
  // administracja nie mają własnej gałęzi, którą można by zawęzić.
  const canNarrowToOwnTeam = coachedTrainerIds.length > 1;
  const narrowToTeam = canNarrowToOwnTeam && teamScope === "team";

  // Sentyment i wycinek zespołu muszą siedzieć w kluczu, inaczej zmiana filtra nie
  // odświeży danych — oba zawężają zapytanie, nie wiersze na ekranie.
  const queryKey = ["chatAlerts", sentiment, narrowToTeam ? coachedTrainerIds : "account"];
  const { data, isLoading } = useQuery(queryKey, {
    enabled,
    keepPreviousData: true,
    queryFn: async () => {
      // `!inner` przy zawężeniu: bez niego filtr po kolumnie osadzonej tylko zeruje
      // embed zamiast odciąć wiersz, więc kolejka wyglądałaby na pełną, tyle że bez nazwisk.
      let query = supabase
        .from("chat_message_metadata")
        .select(
          `*, message:chat_message(authorId, content), trainee:traineeId${narrowToTeam ? "!inner" : ""}(firstName, lastName, email, id, assignedTrainerId)`,
          { count: "exact" },
        )
        .not("handled", "is", true)
        .in("sentiment", sentiment)
        .is("actionable", true)
        .in("context", [
          "workouts",
          "nutrition",
          "workouts_and_nutrition",
          "consultation",
          "churn",
          "subscription_renewal",
          "app_bugs",
        ]);

      if (narrowToTeam) {
        query = query.in("trainee.assignedTrainerId", coachedTrainerIds);
      }

      const { data, count } = await query
        .order("sentiment")
        .order("createdAt", { ascending: false })
        .limit(150)
        .throwOnError()
        .returns<ChatMessageMetadataWithTrainee[]>();

      return {
        messages: data,
        count,
      };
    },
  });

  const updateMessage = async (id: string, data: Partial<ChatMessageMetadata>) => {
    const previousList: { messages: ChatMessageMetadataWithTrainee[] } | undefined = queryClient.getQueryData(queryKey);
    if (previousList) {
      queryClient.setQueryData(queryKey, {
        ...previousList,
        messages: previousList.messages.map((item) => (item.id === id ? { ...item, ...data } : item)),
      });
    }
    // Bez `.select()` odmowa RLS jest nieodróżnialna od sukcesu: UPDATE, który nie
    // trafił w żaden wiersz, nie jest błędem. Optymistyczny ptaszek wracałby wtedy
    // po invalidacji bez słowa wyjaśnienia.
    const { data: updated, error } = await supabase
      .from("chat_message_metadata")
      .update(data)
      .eq("id", id)
      .select("id");

    void queryClient.invalidateQueries(["chatAlerts"]);

    if (error) {
      void message.error("Nie udało się zaktualizować wiadomości");
      return;
    }

    if (!updated?.length) {
      void message.error("Brak uprawnień do zmiany tej wiadomości");
    }
  };

  return {
    data,
    isLoading,
    updateMessage,
    sentiment,
    setSentiment,
    teamScope: narrowToTeam ? ("team" as const) : ("account" as const),
    setTeamScope,
    canNarrowToOwnTeam,
  };
};

interface ChatAlertsTableProps {
  messages: ChatMessageMetadataWithTrainee[];
  isLoading: boolean;
  updateMessage: (id: string, data: Partial<ChatMessageMetadata>) => Promise<void>;
  sentiment: MessageSentiment[];
  onSentimentChange: (sentiment: MessageSentiment[]) => void;
  teamScope?: ChatAlertsTeamScope;
  onTeamScopeChange?: (scope: ChatAlertsTeamScope) => void;
  /** Tylko head coach ma co zawężać — dla pozostałych przełącznik się nie renderuje. */
  canNarrowToOwnTeam?: boolean;
}

const sentimentBadgeStatus: Record<MessageSentiment, "error" | "warning" | "default" | "success"> = {
  highly_negative: "error",
  negative: "warning",
  neutral: "default",
  positive: "success",
};

/** The bare queue, so it can sit in a dashboard tab that already carries the title (TRE-1854). */
export const ChatAlertsTable = ({
  messages,
  isLoading,
  updateMessage,
  sentiment: sentimentFilter,
  onSentimentChange,
  teamScope = "account",
  onTeamScopeChange,
  canNarrowToOwnTeam = false,
}: ChatAlertsTableProps): React.ReactElement => {
  const navigate = useNavigate();
  const { byId } = useTeamMembers();
  const dispatch = useAppDispatch();
  const { isHeadCoach, isAdministration } = useUserRole();
  const fetchingExtraChannel = useAppSelector((store) => store.chat.channelsListStatus.fetchingExtraChannel);
  const trainers = useAppSelector(getTrainersTeam);

  const { t } = useTranslation(["trainees", "dashboard"]);
  // Filtry kolumn w antd są all-or-nothing: gdy JEDNA kolumna dostanie `filteredValue`,
  // antd przestaje trzymać własny stan i czyta propsy, więc niekontrolowane kolumny
  // raportują puste zaznaczenie i cicho przestają filtrować. Sentyment musi być
  // kontrolowany, bo zawęża zapytanie — więc kontekst i trener też, jak w TrainnesTable.
  const [columnFilters, setColumnFilters] = React.useState<Record<string, FilterValue | null>>({});

  const handleChange: TableProps<ChatMessageMetadataWithTrainee>["onChange"] = (_pagination, filters) => {
    setColumnFilters(filters);

    const selected = (filters.sentiment ?? []) as MessageSentiment[];
    // Pusty `.in()` nie zwróciłby nic, więc „Usuń filtry" wraca do domyślnego widoku.
    onSentimentChange(selected.length ? selected : DEFAULT_ALERTS_SENTIMENT);
  };

  return (
    <>
      {canNarrowToOwnTeam && (
        <Space size={8} className="mb-4">
          <Typography.Text type="secondary">Zakres:</Typography.Text>
          <Segmented
            value={teamScope}
            onChange={(value) => onTeamScopeChange?.(value as ChatAlertsTeamScope)}
            options={[
              {
                value: "team",
                label: (
                  <Tooltip title="Twoje klientki i klientki Twoich trenerów">
                    <span>Mój zespół</span>
                  </Tooltip>
                ),
              },
              { value: "account", label: "Całe konto" },
            ]}
          />
        </Space>
      )}
      <Table
        locale={{
          emptyText: "Lista wiadomości do weryfikacji jest pusta",
        }}
        pagination={false}
        dataSource={messages}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={isLoading}
        onChange={handleChange}
      >
        <Table.Column<ChatMessageMetadataWithTrainee>
          title={t("tableHeader.name")}
          dataIndex={["trainee", "firstName"]}
          key="name"
          render={(_, row) => (
            <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.traineeId}`)}>
              {row.trainee?.firstName} {row.trainee?.lastName}
            </Button>
          )}
        />
        <Table.Column
          title="Data wiadomości"
          dataIndex="createdAt"
          key="createdAt"
          render={(createdAt) => <span>{dayjs(createdAt).format("DD.MM.YYYY HH:mm")}</span>}
        />

        <Table.Column<ChatMessageMetadataWithTrainee>
          title={t("tableHeader.summary")}
          dataIndex="summary"
          key="summary"
          render={(summary, row) => <Tooltip title={row.message.content}>{summary}</Tooltip>}
        />

        <Table.Column
          title={t("tableHeader.sentiment")}
          dataIndex="sentiment"
          key="sentiment"
          filterMultiple
          // Zawęża zapytanie, nie wiersze na ekranie — stąd brak `onFilter`.
          filteredValue={sentimentFilter}
          filters={(["highly_negative", "negative", "neutral", "positive"] as MessageSentiment[]).map((value) => ({
            text: t(`dashboard:sentiment.${value}`),
            value,
          }))}
          render={(sentiment: MessageSentiment) => {
            return (
              <Badge
                className="whitespace-pre"
                status={sentimentBadgeStatus[sentiment] ?? "warning"}
                text={t(`dashboard:sentiment.${sentiment}`)}
              />
            );
          }}
        />
        <Table.Column<ChatMessageMetadataWithTrainee>
          title={t("tableHeader.context")}
          dataIndex="context"
          key="context"
          filterMultiple
          filteredValue={columnFilters.context ?? null}
          filters={[
            {
              text: t("dashboard:context.general"),
              value: "general",
            },
            {
              text: t("dashboard:context.workouts"),
              value: "workouts",
            },
            {
              text: t("dashboard:context.nutrition"),
              value: "nutrition",
            },
            {
              text: t("dashboard:context.workouts_and_nutrition"),
              value: "workouts_and_nutrition",
            },
            {
              text: t("dashboard:context.consultation"),
              value: "consultation",
            },
            {
              text: t("dashboard:context.churn"),
              value: "churn",
            },
            {
              text: t("dashboard:context.subscription_renewal"),
              value: "subscription_renewal",
            },
            {
              text: t("dashboard:context.private_life"),
              value: "private_life",
            },
            {
              text: t("dashboard:context.app_bugs"),
              value: "app_bugs",
            },
            {
              text: t("dashboard:context.other"),
              value: "other",
            },
          ]}
          onFilter={(value, record) => record.context === value || (!record.context && value === "other")}
          render={(context) =>
            context ? <Tag color={context === "churn" ? "red" : undefined}>{t(`dashboard:context.${context}`)}</Tag> : "-"
          }
        />
        <Table.Column<ChatMessageMetadataWithTrainee>
          title={t("tableHeader.trainer")}
          dataIndex={["trainee", "assignedTrainerId"]}
          filterMultiple
          filterSearch
          filteredValue={columnFilters.assignedTrainerId ?? null}
          filters={createTrainerOptions(trainers, t, false)}
          onFilter={(value, record) => {
            return (
              record.trainee?.assignedTrainerId === value ||
              Boolean(
                record.trainee?.assignedTrainerId &&
                Array.isArray(value) &&
                value.includes(record.trainee.assignedTrainerId),
              )
            );
          }}
          key="assignedTrainerId"
          render={(trainerId) => {
            const trainer = byId[trainerId];

            if (!trainer) {
              return "-";
            }

            return (
              <Tag color="magenta">
                {trainer.firstName} {trainer.lastName}
              </Tag>
            );
          }}
        />
        <Table.Column<ChatMessageMetadataWithTrainee>
          title={t("tableHeader.status")}
          dataIndex={["handled"]}
          key="handled"
          render={(handled, row) => {
            return handled ? (
              <CheckOutlined style={{ color: "#25b10f" }} />
            ) : (
              <Space>
                <CloseOutlined style={{ color: "#ED1C24" }} />
                <Button type="dashed" size="small" onClick={() => updateMessage(row.id, { handled: true })}>
                  Oznacz jako obsłużone
                </Button>
              </Space>
            );
          }}
        />
        <Table.Column<ChatMessageMetadataWithTrainee>
          title={t("tableHeader.options")}
          dataIndex="action"
          key="action"
          render={(_, row) => (
            <Space size={4}>
              <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.traineeId}`)}>
                {t(`common:details`)}
              </Button>
              <Button
                icon={<ExportOutlined />}
                title="Otwórz w nowy oknie"
                href={`${window.location.origin}/trainee/details/${row.traineeId}`}
                target="_blank"
                type="link"
              />

              {row.channelId && (
                <Button
                  icon={<MessageOutlined />}
                  style={{ paddingLeft: 2, paddingRight: 2 }}
                  title="Wyślij wiadomość"
                  loading={fetchingExtraChannel}
                  onClick={() =>
                    dispatch(
                      chatActions.findAndAddChannelToList({
                        // Kanału szuka się po kotwicy konta, a nie po sobie: ani head
                        // coach, ani administracja nie są w `assignedUsers` kanału
                        // klientki. Dla właściciela obie kotwice to ten sam id.
                        userId: row.message.authorId,
                        channelId: String(row.channelId),
                        isHeadCoach: isHeadCoach || isAdministration,
                      }),
                    )
                  }
                  type="link"
                />
              )}
            </Space>
          )}
        />
      </Table>
    </>
  );
};

/** Standalone card for the account owner's stats dashboard, which has no tabs. */
export const ChatAlerts = (): React.ReactElement | null => {
  // Jedyna bramka widgetu — dostęp nadaje się per osoba w drawerze uprawnień,
  // nie rolą, więc żadne miejsce renderujące nie powtarza już tego warunku.
  const canReadAlerts = usePermissionRequirement(dashboardPermissionRequirements.chatAlerts);
  const { data, isLoading, updateMessage, sentiment, setSentiment, teamScope, setTeamScope, canNarrowToOwnTeam } =
    useChatAlerts(canReadAlerts);

  if (!canReadAlerts) {
    return null;
  }

  return (
    <Card>
      <Title className="mb-6">
        Zastrzeżenia klientów do weryfikacji {data?.count && data.count > 0 ? `(${data.count})` : ""}
      </Title>
      <ChatAlertsTable
        messages={data?.messages ?? []}
        isLoading={isLoading}
        updateMessage={updateMessage}
        sentiment={sentiment}
        onSentimentChange={setSentiment}
        teamScope={teamScope}
        onTeamScopeChange={setTeamScope}
        canNarrowToOwnTeam={canNarrowToOwnTeam}
      />
    </Card>
  );
};
